import { api } from './api';
import type { Etapa1Identifier } from './validacao-etapa1.service';

export interface VotacaoEtapa1Lock {
  _id: string;
  prefixo?: string;
  comite?: string;
  categoria: string;
  lockedBy: string;
  lockedByName: string;
  lockedByMatricula: string;
  lockedAt: number;
  heartbeat: number;
  status: 'active' | 'released' | 'completed';
}

const COLLECTION = 'votacao_etapa1_lock__c';
export const LOCK_TIMEOUT_MS = 5 * 60 * 1000;
export const HEARTBEAT_INTERVAL_MS = 60 * 1000;
export const LOCK_CHECK_INTERVAL_MS = 30 * 1000;

const identifierToMatch = (identifier: Etapa1Identifier): Record<string, string> | null => {
  const comite = identifier?.comite ? String(identifier.comite).trim() : '';
  if (comite) return { comite };
  const prefixo = identifier?.prefixo ? String(identifier.prefixo).trim() : '';
  if (prefixo) return { prefixo };
  return null;
};

const isLockExpired = (lock: VotacaoEtapa1Lock): boolean => {
  return Date.now() - lock.heartbeat > LOCK_TIMEOUT_MS;
};

export const checkLockForPrefixo = async (
  identifier: Etapa1Identifier,
  categoria: string
): Promise<VotacaoEtapa1Lock | null> => {
  const match = identifierToMatch(identifier);
  if (!match) return null;

  const pipeline = [
    { $match: { ...match, categoria, status: 'active' } },
    { $sort: { lockedAt: -1 } },
    { $limit: 1 },
  ];

  const { data } = await api.post(
    `/database/${COLLECTION}/aggregate`,
    pipeline
  );

  const results = Array.isArray(data) ? data : [];
  if (results.length === 0) return null;

  const lock = results[0] as VotacaoEtapa1Lock;

  if (isLockExpired(lock)) {
    markLockAsReleased(lock._id).catch(() => {});
    return null;
  }

  return lock;
};

export const acquireLock = async (
  identifier: Etapa1Identifier,
  categoria: string,
  userId: string,
  userName: string,
  userMatricula: string
): Promise<VotacaoEtapa1Lock> => {
  const match = identifierToMatch(identifier);
  if (!match) {
    throw new Error('Identificador inválido para aquisição de lock.');
  }

  const existing = await checkLockForPrefixo(identifier, categoria);

  if (existing) {
    if (existing.lockedBy === userId) {
      return existing;
    }
    throw new LockHeldError(existing);
  }

  const now = Date.now();
  const doc = {
    ...match,
    categoria,
    lockedBy: userId,
    lockedByName: userName,
    lockedByMatricula: userMatricula,
    lockedAt: now,
    heartbeat: now,
    status: 'active' as const,
  };

  const { data: created } = await api.post(`/database/${COLLECTION}`, doc);

  const raceCheck = await getAllActiveLocksForPrefixo(identifier, categoria);

  if (raceCheck.length > 1) {
    const sorted = raceCheck.sort((a, b) => a.lockedAt - b.lockedAt);
    const winner = sorted[0];

    if (winner._id !== created._id) {
      await markLockAsReleased(created._id).catch(() => {});
      throw new LockHeldError(winner);
    }

    for (let i = 1; i < sorted.length; i++) {
      markLockAsReleased(sorted[i]._id).catch(() => {});
    }
  }

  return created as VotacaoEtapa1Lock;
};

export const renewHeartbeat = async (lockId: string): Promise<void> => {
  await api.put(`/database/${COLLECTION}`, {
    _id: lockId,
    heartbeat: Date.now(),
  });
};

export const markLockAsReleased = async (lockId: string): Promise<void> => {
  await api.put(`/database/${COLLECTION}`, {
    _id: lockId,
    status: 'released',
  });
};

export const markLockAsCompleted = async (lockId: string): Promise<void> => {
  await api.put(`/database/${COLLECTION}`, {
    _id: lockId,
    status: 'completed',
  });
};

export const checkCompletedForPrefixo = async (
  identifier: Etapa1Identifier,
  categoria: string
): Promise<VotacaoEtapa1Lock | null> => {
  const match = identifierToMatch(identifier);
  if (!match) return null;

  const pipeline = [
    { $match: { ...match, categoria, status: 'completed' } },
    { $limit: 1 },
  ];

  const { data } = await api.post(
    `/database/${COLLECTION}/aggregate`,
    pipeline
  );

  const results = Array.isArray(data) ? data : [];
  return results.length > 0 ? (results[0] as VotacaoEtapa1Lock) : null;
};

export class LockHeldError extends Error {
  public lock: VotacaoEtapa1Lock;

  constructor(lock: VotacaoEtapa1Lock) {
    super(`Votacao bloqueada por ${lock.lockedByName}`);
    this.name = 'LockHeldError';
    this.lock = lock;
  }
}

export const checkAllCompletedForPrefixo = async (
  identifier: Etapa1Identifier,
  categorias: string[]
): Promise<VotacaoEtapa1Lock[]> => {
  const match = identifierToMatch(identifier);
  if (!match) return [];

  const pipeline = [
    { $match: { ...match, status: 'completed', categoria: { $in: categorias } } },
  ];

  const { data } = await api.post(
    `/database/${COLLECTION}/aggregate`,
    pipeline
  );

  return Array.isArray(data) ? data : [];
};

const getAllActiveLocksForPrefixo = async (
  identifier: Etapa1Identifier,
  categoria: string
): Promise<VotacaoEtapa1Lock[]> => {
  const match = identifierToMatch(identifier);
  if (!match) return [];

  const pipeline = [
    { $match: { ...match, categoria, status: 'active' } },
    { $sort: { lockedAt: 1 } },
  ];

  const { data } = await api.post(
    `/database/${COLLECTION}/aggregate`,
    pipeline
  );

  return Array.isArray(data) ? data : [];
};
